import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  Skeleton,
  ImageList,
  ImageListItem,
} from '@mui/material';
import {
  ArrowBack,
  LocationOn,
  Visibility,
  Category,
  Phone,
  Email,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';
import api from '../../services/api';
import { format } from 'date-fns';

interface Listing {
  id: string;
  title: string;
  description: string;
  price: number;
  category: string;
  category_id: number;
  thumbnail_url: string;
  images: string[];
  location: string;
  status: string;
  views: number;
  inquiries_count: number;
  created_at: string;
  seller_name: string;
  prefer_phone: boolean;
  service_user_id: string;
}

interface ContactForm {
  buyer_name: string;
  buyer_email: string;
  buyer_phone: string;
  message: string;
}

const ListingDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [listing, setListing] = useState<Listing | null>(null);
  const [loading, setLoading] = useState(true);
  const [contactOpen, setContactOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [contactForm, setContactForm] = useState<ContactForm>({
    buyer_name: '',
    buyer_email: '',
    buyer_phone: '',
    message: '',
  });
  const [contactLoading, setContactLoading] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);

  useEffect(() => {
    if (id) {
      fetchListing();
    }
  }, [id]);

  const fetchListing = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/marketplace/listings/${id}`);
      setListing(response.data);
    } catch (error) {
      console.error('Error fetching listing:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleContactSeller = async () => {
    setContactLoading(true);
    try {
      await api.post(`/marketplace/listings/${id}/contact`, contactForm);
      setContactSuccess(true);
      setTimeout(() => {
        setContactOpen(false);
        setContactSuccess(false);
        setContactForm({
          buyer_name: '',
          buyer_email: '',
          buyer_phone: '',
          message: '',
        });
      }, 2000);
    } catch (error) {
      console.error('Error contacting seller:', error);
    } finally {
      setContactLoading(false);
    }
  };

  const handlePrevImage = () => {
    setSelectedImageIndex((prev) => 
      prev === 0 ? (listing?.images.length || 1) - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setSelectedImageIndex((prev) => 
      prev === (listing?.images.length || 1) - 1 ? 0 : prev + 1
    );
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
  };

  const getPlaceholderImage = () => {
    return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAwIiBoZWlnaHQ9IjQwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNjAwIiBoZWlnaHQ9IjQwMCIgZmlsbD0iI2VlZSIvPjx0ZXh0IHRleHQtYW5jaG9yPSJtaWRkbGUiIHg9IjMwMCIgeT0iMjAwIiBzdHlsZT0iZmlsbDojYWFhO2ZvbnQtd2VpZ2h0OmJvbGQ7Zm9udC1zaXplOjI0cHg7Zm9udC1mYW1pbHk6QXJpYWwsSGVsdmV0aWNhLHNhbnMtc2VyaWY7ZG9taW5hbnQtYmFzZWxpbmU6Y2VudHJhbCI+Tm8gSW1hZ2U8L3RleHQ+PC9zdmc+';
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Skeleton variant="rectangular" height={400} sx={{ mb: 2 }} />
        <Skeleton variant="text" height={60} />
        <Skeleton variant="text" />
        <Skeleton variant="text" />
      </Container>
    );
  }

  if (!listing) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">Listing not found</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/app/marketplace')} sx={{ mt: 2 }}>
          Back to {t('marketplace.title')}
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/app/marketplace')}
        sx={{ mb: 2 }}
      >
        Back to {t('marketplace.title')}
      </Button>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
        {/* Images Section */}
        <Box sx={{ flex: { md: '1 1 60%' } }}>
          <Paper sx={{ p: 2 }}>
            {listing.images && listing.images.length > 0 ? (
              <>
                <Box sx={{ position: 'relative', mb: 2 }}>
                  <img
                    src={listing.images[selectedImageIndex]}
                    alt={listing.title}
                    style={{
                      width: '100%',
                      height: 'auto',
                      maxHeight: '500px',
                      objectFit: 'contain',
                    }}
                  />
                  {listing.images.length > 1 && (
                    <>
                      <IconButton
                        sx={{
                          position: 'absolute',
                          left: 8,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          bgcolor: 'background.paper',
                          '&:hover': { bgcolor: 'background.paper' },
                        }}
                        onClick={handlePrevImage}
                      >
                        <ChevronLeft />
                      </IconButton>
                      <IconButton
                        sx={{
                          position: 'absolute',
                          right: 8,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          bgcolor: 'background.paper',
                          '&:hover': { bgcolor: 'background.paper' },
                        }}
                        onClick={handleNextImage}
                      >
                        <ChevronRight />
                      </IconButton>
                    </>
                  )}
                </Box>
                {listing.images.length > 1 && (
                  <ImageList sx={{ height: 100 }} cols={listing.images.length} rowHeight={100}>
                    {listing.images.map((image, index) => (
                      <ImageListItem
                        key={index}
                        sx={{
                          cursor: 'pointer',
                          opacity: selectedImageIndex === index ? 1 : 0.6,
                          '&:hover': { opacity: 1 },
                        }}
                        onClick={() => setSelectedImageIndex(index)}
                      >
                        <img
                          src={image}
                          alt={`${listing.title} ${index + 1}`}
                          style={{ height: '100%', objectFit: 'cover' }}
                        />
                      </ImageListItem>
                    ))}
                  </ImageList>
                )}
              </>
            ) : (
              <img
                src={getPlaceholderImage()}
                alt="No content available"
                style={{ width: '100%', height: '400px', objectFit: 'cover' }}
              />
            )}
          </Paper>
        </Box>

        {/* Details Section */}
        <Box sx={{ flex: { md: '1 1 40%' } }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
              {listing.title}
            </Typography>
            
            <Typography variant="h3" color="primary" gutterBottom>
              {formatPrice(listing.price)}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              {listing.category && (
                <Chip
                  icon={<Category />}
                  label={listing.category}
                  variant="outlined"
                />
              )}
              {listing.location && (
                <Chip
                  icon={<LocationOn />}
                  label={listing.location}
                  variant="outlined"
                />
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Visibility fontSize="small" sx={{ mr: 0.5 }} />
                <Typography variant="body2" color="text.secondary">
                  {listing.views} {t('marketplace.listing.views')}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {t('marketplace.listing.listedOn')} {format(new Date(listing.created_at), 'MMM dd, yyyy')}
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('marketplace.listing.seller')}: {listing.seller_name}
            </Typography>

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={() => setContactOpen(true)}
              startIcon={listing.prefer_phone ? <Phone /> : <Email />}
              sx={{ mt: 3, mb: 2 }}
            >
              {t('marketplace.listing.contactSeller')}
            </Button>

          </Paper>

          <Paper sx={{ p: 3, mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t('marketplace.listing.description')}
            </Typography>
            <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
              {listing.description}
            </Typography>
          </Paper>
        </Box>
      </Box>

      {/* Contact Dialog */}
      <Dialog open={contactOpen} onClose={() => setContactOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('marketplace.contact.title')}</DialogTitle>
        <DialogContent>
          {contactSuccess ? (
            <Alert severity="success">
              {t('marketplace.contact.success')}
            </Alert>
          ) : (
            <Box sx={{ pt: 2 }}>
              <TextField
                fullWidth
                label={t('marketplace.contact.yourName')}
                value={contactForm.buyer_name}
                onChange={(e) => setContactForm({ ...contactForm, buyer_name: e.target.value })}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label={t('marketplace.contact.yourEmail')}
                type="email"
                value={contactForm.buyer_email}
                onChange={(e) => setContactForm({ ...contactForm, buyer_email: e.target.value })}
                margin="normal"
              />
              <TextField
                fullWidth
                label={t('marketplace.contact.yourPhone')}
                value={contactForm.buyer_phone}
                onChange={(e) => setContactForm({ ...contactForm, buyer_phone: e.target.value })}
                margin="normal"
              />
              <TextField
                fullWidth
                label={t('marketplace.contact.message')}
                multiline
                rows={4}
                value={contactForm.message}
                onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                margin="normal"
                required
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                {t('marketplace.contact.provideContact')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactOpen(false)}>{t('marketplace.contact.cancel')}</Button>
          <Button
            onClick={handleContactSeller}
            variant="contained"
            disabled={
              contactLoading ||
              contactSuccess ||
              !contactForm.buyer_name ||
              !contactForm.message ||
              (!contactForm.buyer_email && !contactForm.buyer_phone)
            }
          >
            {contactLoading ? t('marketplace.contact.sending') : t('marketplace.contact.send')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ListingDetailPage;